import { ACTIVITY_ACTOR_TYPE } from '@trustero/trustero-api-web/lib/activity/activity_pb'

export const AI_AGENT_SUFFIX = '(via Trustero AI)'

/**
 * The name shown for whoever performed an activity, given the entry's actor and actor
 * type. `userName` is the display name of the user whose email is `actor`, when known.
 *
 * An AI agent's entry carries the email of the user it acted for, and reads as that
 * user "via Trustero AI" so it is never mistaken for the user's own change.
 */
export const getActivityActorLabel = (
  actor: string,
  actorType: number | undefined,
  userName?: string,
): string => {
  const person = userName || actor
  switch (actorType) {
    case ACTIVITY_ACTOR_TYPE.AI_AGENT:
      return person ? `${person} ${AI_AGENT_SUFFIX}` : 'Trustero AI'
    case ACTIVITY_ACTOR_TYPE.TRUSTERO:
      return actor || 'Trustero'
    case ACTIVITY_ACTOR_TYPE.RECEPTOR:
      return actor || 'Receptor'
    case ACTIVITY_ACTOR_TYPE.SCHEDULER:
      return 'Scheduler'
    case ACTIVITY_ACTOR_TYPE.EXTERNAL:
      return actor ? `${actor} (external)` : 'External'
    case ACTIVITY_ACTOR_TYPE.PLAYBOOK:
      return actor || 'Playbook'
    case ACTIVITY_ACTOR_TYPE.USER:
      return person || 'System'
    default:
      return actor || 'System'
  }
}

/** The actor type's name for exports (e.g. "User", "AI Agent"). */
export const getActivityActorTypeName = (
  actorType: number | undefined,
): string => {
  switch (actorType) {
    case ACTIVITY_ACTOR_TYPE.USER:
      return 'User'
    case ACTIVITY_ACTOR_TYPE.AI_AGENT:
      return 'AI Agent'
    case ACTIVITY_ACTOR_TYPE.TRUSTERO:
      return 'Trustero'
    case ACTIVITY_ACTOR_TYPE.RECEPTOR:
      return 'Receptor'
    case ACTIVITY_ACTOR_TYPE.SCHEDULER:
      return 'Scheduler'
    case ACTIVITY_ACTOR_TYPE.EXTERNAL:
      return 'External'
    case ACTIVITY_ACTOR_TYPE.PLAYBOOK:
      return 'Playbook'
    default:
      return 'Unknown'
  }
}

/**
 * The value an entry is filtered by in the actor filter. This is the raw actor (an email
 * for people) so filtering by a person includes the changes an AI agent made on their
 * behalf. Entries with no actor (e.g. the scheduler) fall back to their label.
 */
export const getActivityActorKey = (
  actor: string,
  actorType: number | undefined,
): string => actor || getActivityActorLabel('', actorType)
